import { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
    Card,
    CardBody,
    CardHeader,
    CardFooter,
    Button,
    Autocomplete,
    AutocompleteItem,
    DatePicker,
    NumberInput,
    Textarea,
    Spinner,
    Chip,
} from "@heroui/react";
import { LuArrowLeft, LuSave } from "react-icons/lu";
import axios from "axios";
import { usePostData, useFetchData } from "@/hooks";
import { showToast } from "@/utils";
import { ThemedText, PageHeader, ErrorState } from "@/components/ui";
import { parseDate, getLocalTimeZone, today } from "@internationalized/date";
import { BASE_API } from "@/constants";

const StockOutEdit = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    const [unitUrl, setUnitUrl] = useState(null);
    const [balance, setBalance] = useState(null);
    const [isCheckingBalance, setIsCheckingBalance] = useState(false);
    const [initialStockKey, setInitialStockKey] = useState("");
    const [initialQtyOut, setInitialQtyOut] = useState(0);

    const {
        handleSubmit,
        control,
        watch,
        setValue,
        reset,
        formState: { errors },
    } = useForm({
        defaultValues: {
            material_id: "",
            warehouse_id: "",
            material_unit_id: "",
            date: null,
            qty_out: null,
            description: "",
        },
    });

    const watchData = watch();

    const {
        data: initialData,
        isLoading: isFetching,
        error: fetchError,
    } = useFetchData(`stock-out/${id}/edit`);

    const { data: materials, isLoading: isLoadingMaterials } =
        useFetchData("select/material");
    const { data: warehouses, isLoading: isLoadingWarehouses } =
        useFetchData("select/warehouse");
    const { data: units, isLoading: isLoadingUnits } = useFetchData(unitUrl);

    const updateMutation = usePostData(`stock-out/${id}/update`);

    const getStockKey = (data) =>
        [
            data.warehouse_id || "",
            data.material_id || "",
            data.material_unit_id || "",
        ].join("|");

    const fetchBalance = async (data, updateState = true) => {
        if (
            !data?.warehouse_id ||
            !data?.material_id ||
            !data?.material_unit_id
        ) {
            if (updateState) setBalance(null);
            return null;
        }

        if (updateState) setIsCheckingBalance(true);

        try {
            const response = await axios.get(
                `${BASE_API}/stocks/check-material-balance`,
                {
                    params: {
                        warehouse_id: data.warehouse_id,
                        material_id: data.material_id,
                        material_unit_id: data.material_unit_id,
                    },
                },
            );
            const nextBalance = Number(response.data.data?.balance || 0);
            if (updateState) setBalance(nextBalance);
            return nextBalance;
        } catch (error) {
            console.error("Failed to fetch stock balance:", error);
            if (updateState) setBalance(null);
            return null;
        } finally {
            if (updateState) setIsCheckingBalance(false);
        }
    };

    useEffect(() => {
        if (initialData?.material_id) {
            setUnitUrl(
                `select/material-unit?material_id=${initialData.material_id}`,
            );
        }
    }, [initialData]);

    useEffect(() => {
        if (initialData) {
            const formData = {
                material_id: initialData.material_id
                    ? String(initialData.material_id)
                    : "",
                warehouse_id: initialData.warehouse_id
                    ? String(initialData.warehouse_id)
                    : "",
                material_unit_id: initialData.material_unit_id
                    ? String(initialData.material_unit_id)
                    : "",
                date: initialData.date || null,
                qty_out: initialData.qty_out || null,
                description: initialData.description || "",
            };

            reset(formData);
            setInitialStockKey(getStockKey(formData));
            setInitialQtyOut(Number(initialData.qty_out || 0));
            fetchBalance(formData);
        }
    }, [initialData, reset]);

    const getAvailableForEdit = (data, currentBalance = balance) => {
        const baseBalance = Number(currentBalance || 0);
        return getStockKey(data) === initialStockKey
            ? baseBalance + initialQtyOut
            : baseBalance;
    };

    const handleMaterialChange = (key) => {
        setValue("material_id", key || "");
        setValue("material_unit_id", "");
        if (key) {
            setUnitUrl(`select/material-unit?material_id=${key}`);
        } else {
            setUnitUrl(null);
        }
        fetchBalance({
            ...watchData,
            material_id: key || "",
            material_unit_id: "",
        });
    };

    const handleUnitChange = (key) => {
        setValue("material_unit_id", key || "");
        fetchBalance({ ...watchData, material_unit_id: key || "" });
    };

    const handleWarehouseChange = (key) => {
        setValue("warehouse_id", key || "");
        fetchBalance({ ...watchData, warehouse_id: key || "" });
    };

    const onSubmit = async (data) => {
        const currentBalance = await fetchBalance(data, false);
        if (currentBalance === null) {
            showToast(
                "error",
                "Gagal Mengecek Stok",
                "Terjadi kesalahan saat mengecek stok tersedia.",
            );
            return;
        }

        const availableForEdit = getAvailableForEdit(data, currentBalance);
        if (Number(data.qty_out || 0) > availableForEdit) {
            showToast(
                "error",
                "Stok Tidak Cukup",
                `Stok tersedia hanya ${availableForEdit}, sedangkan qty keluar ${data.qty_out}.`,
            );
            return;
        }

        const payload = {
            material_id: data.material_id,
            warehouse_id: data.warehouse_id,
            material_unit_id: data.material_unit_id,
            date: data.date,
            qty_out: Number(data.qty_out),
            description: data.description || null,
        };

        updateMutation.mutate(payload, {
            method: "put",
            onSuccess: () => {
                showToast(
                    "success",
                    "Berhasil",
                    "Stok keluar berhasil diperbarui.",
                );
                navigate("/stock-out");
            },
            onError: (err) => {
                const message =
                    err.response?.status === 422
                        ? err.response?.data?.message ||
                          "Mohon periksa kembali input Anda. Pastikan stok tersedia mencukupi."
                        : "Terjadi kesalahan pada sistem. Silakan coba beberapa saat lagi.";

                showToast("error", "Gagal", message);
            },
        });
    };

    if (fetchError) {
        return (
            <ErrorState
                title="Gagal Memuat Data"
                description="Terjadi kesalahan saat memuat data. Silakan coba lagi."
                onAction={() => navigate("/stock-out")}
                actionLabel="Kembali"
            />
        );
    }

    return (
        <div className="mx-auto flex max-w-6xl flex-col gap-6">
            <div className="flex gap-4">
                <Button isIconOnly variant="light" onPress={() => navigate(-1)}>
                    <LuArrowLeft size={20} />
                </Button>
                <PageHeader
                    title="Edit Stok Keluar"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Stok Keluar", href: "/stock-out" },
                        { label: "Edit" },
                    ]}
                />
            </div>

            <form onSubmit={handleSubmit(onSubmit)} noValidate>
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="bg-default-100/70 px-6 py-4">
                        <ThemedText className="font-semibold">
                            Detail Stok Keluar
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="p-6">
                        {isFetching ? (
                            <div className="flex h-48 w-full items-center justify-center">
                                <Spinner
                                    label="Memuat data..."
                                    color="primary"
                                />
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-4 md:grid-cols-6 xl:grid-cols-12">
                                <Controller
                                    name="material_id"
                                    control={control}
                                    rules={{
                                        required: "Produk wajib dipilih",
                                    }}
                                    render={({ field }) => (
                                        <Autocomplete
                                            label="Produk"
                                            placeholder="Cari & pilih produk..."
                                            isRequired
                                            isDisabled
                                            isLoading={isLoadingMaterials}
                                            selectedKey={
                                                field.value
                                                    ? String(field.value)
                                                    : null
                                            }
                                            onSelectionChange={(key) => {
                                                field.onChange(key || "");
                                                handleMaterialChange(key);
                                            }}
                                            variant="bordered"
                                            isInvalid={!!errors.material_id}
                                            errorMessage={
                                                errors.material_id?.message
                                            }
                                            className="md:col-span-6 xl:col-span-6"
                                        >
                                            {(materials || []).map((m) => (
                                                <AutocompleteItem
                                                    key={String(m.value)}
                                                    value={m.value}
                                                >
                                                    {m.title}
                                                </AutocompleteItem>
                                            ))}
                                        </Autocomplete>
                                    )}
                                />

                                <Controller
                                    name="warehouse_id"
                                    control={control}
                                    rules={{
                                        required: "Gudang wajib dipilih",
                                    }}
                                    render={({ field }) => (
                                        <Autocomplete
                                            label="Gudang"
                                            placeholder="Pilih lokasi"
                                            isRequired
                                            isDisabled
                                            isLoading={isLoadingWarehouses}
                                            selectedKey={
                                                field.value
                                                    ? String(field.value)
                                                    : null
                                            }
                                            onSelectionChange={(key) => {
                                                field.onChange(key || "");
                                                handleWarehouseChange(key);
                                            }}
                                            variant="bordered"
                                            isInvalid={!!errors.warehouse_id}
                                            errorMessage={
                                                errors.warehouse_id?.message
                                            }
                                            className="md:col-span-3 xl:col-span-3"
                                        >
                                            {(warehouses || []).map((w) => (
                                                <AutocompleteItem
                                                    key={String(w.value)}
                                                    value={w.value}
                                                >
                                                    {w.title}
                                                </AutocompleteItem>
                                            ))}
                                        </Autocomplete>
                                    )}
                                />

                                <Controller
                                    name="date"
                                    control={control}
                                    rules={{
                                        required: "Tanggal wajib diisi",
                                    }}
                                    render={({ field }) => (
                                        <DatePicker
                                            label="Tanggal"
                                            isRequired
                                            showMonthAndYearPickers
                                            value={
                                                field.value
                                                    ? parseDate(field.value)
                                                    : null
                                            }
                                            onChange={(date) =>
                                                field.onChange(
                                                    date ? date.toString() : "",
                                                )
                                            }
                                            maxValue={today(getLocalTimeZone())}
                                            variant="bordered"
                                            isInvalid={!!errors.date}
                                            errorMessage={errors.date?.message}
                                            className="md:col-span-3 xl:col-span-3"
                                        />
                                    )}
                                />

                                <div className="flex flex-col gap-2 md:col-span-3 xl:col-span-6">
                                    <Controller
                                        name="qty_out"
                                        control={control}
                                        rules={{
                                            required: "Qty wajib diisi",
                                            min: {
                                                value: 0.01,
                                                message:
                                                    "Qty harus lebih dari 0",
                                            },
                                        }}
                                        render={({ field }) => (
                                            <NumberInput
                                                label="Qty Keluar"
                                                placeholder="Contoh: 100"
                                                isRequired
                                                isClearable
                                                value={field.value ?? undefined}
                                                onValueChange={(val) =>
                                                    field.onChange(val)
                                                }
                                                variant="bordered"
                                                minValue={0}
                                                hideStepper
                                                isWheelDisabled
                                                isInvalid={!!errors.qty_out}
                                                errorMessage={
                                                    errors.qty_out?.message
                                                }
                                                description={
                                                    isCheckingBalance
                                                        ? "Mengecek stok tersedia..."
                                                        : undefined
                                                }
                                            />
                                        )}
                                    />
                                    {!isCheckingBalance && balance !== null && (
                                        <Chip
                                            size="sm"
                                            variant="flat"
                                            color={
                                                getAvailableForEdit(watchData) >
                                                0
                                                    ? "success"
                                                    : "warning"
                                            }
                                            className="w-fit"
                                        >
                                            Stok tersedia:{" "}
                                            {getAvailableForEdit(watchData)}
                                        </Chip>
                                    )}
                                </div>

                                <Controller
                                    name="material_unit_id"
                                    control={control}
                                    rules={{
                                        required: "Satuan wajib dipilih",
                                    }}
                                    render={({ field }) => (
                                        <Autocomplete
                                            label="Satuan"
                                            placeholder={
                                                watchData.material_id
                                                    ? "Pilih satuan"
                                                    : "Pilih produk dulu"
                                            }
                                            isRequired
                                            isDisabled={!watchData.material_id}
                                            isLoading={isLoadingUnits}
                                            selectedKey={
                                                field.value
                                                    ? String(field.value)
                                                    : null
                                            }
                                            onSelectionChange={(key) => {
                                                field.onChange(key || "");
                                                handleUnitChange(key);
                                            }}
                                            variant="bordered"
                                            isInvalid={
                                                !!errors.material_unit_id
                                            }
                                            errorMessage={
                                                errors.material_unit_id?.message
                                            }
                                            className="md:col-span-3 xl:col-span-6"
                                        >
                                            {(units || []).map((u) => (
                                                <AutocompleteItem
                                                    key={String(u.value)}
                                                    value={u.value}
                                                >
                                                    {u.title}
                                                </AutocompleteItem>
                                            ))}
                                        </Autocomplete>
                                    )}
                                />

                                <Controller
                                    name="description"
                                    control={control}
                                    render={({ field }) => (
                                        <Textarea
                                            label="Keterangan"
                                            placeholder="Keterangan tambahan (opsional)"
                                            value={field.value || ""}
                                            onValueChange={(val) =>
                                                field.onChange(val)
                                            }
                                            variant="bordered"
                                            className="md:col-span-6 xl:col-span-12"
                                        />
                                    )}
                                />
                            </div>
                        )}
                    </CardBody>
                    <CardFooter className="flex justify-end gap-3 px-6 py-4">
                        <Button
                            as={Link}
                            href="/stock-out"
                            type="button"
                            variant="flat"
                            size="sm"
                        >
                            Batal
                        </Button>
                        <Button
                            color="primary"
                            type="submit"
                            isLoading={updateMutation.isLoading}
                            startContent={<LuSave size={16} />}
                            size="sm"
                        >
                            Simpan Perubahan
                        </Button>
                    </CardFooter>
                </Card>
            </form>
        </div>
    );
};

export default StockOutEdit;
